import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary] render failure:", error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-charcoal px-6 py-20 text-cream">
        <div className="max-w-lg text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sage mb-3">
            Something went wrong
          </p>
          <h2 className="font-heading text-2xl md:text-3xl mb-4">
            We hit a snag loading this page.
          </h2>
          <p className="text-sm text-cream/60 mb-6">
            Try refreshing — if the problem persists, please reach us at{" "}
            <a href="mailto:nick@greenleafdigital.net" className="text-sage underline underline-offset-4">
              nick@greenleafdigital.net
            </a>
            .
          </p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center rounded-lg bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-md shadow-primary/20 hover:bg-forest-light transition-colors"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
